import { useState, useEffect } from "react";
import { Smartphone, HelpCircle } from "lucide-react";

/* DateWise Wingman — in-app PWA install helper.
 *
 * Behavior:
 *  - Listens for `beforeinstallprompt` (Chrome/Edge/Samsung Internet).
 *  - When the event fires, shows a visible "Install DateWise on My Phone"
 *    button. Tapping it calls prompt() on the saved event, awaits the
 *    user choice, then clears the event (per spec — single-use).
 *  - When the app is launched in standalone mode (already installed), the
 *    helper hides itself.
 *  - When `beforeinstallprompt` is unavailable (iOS Safari, Firefox iOS,
 *    in-app browsers), falls back to an expandable help line with the
 *    correct platform-specific instructions.
 */

const isIOS = () =>
  typeof navigator !== "undefined" &&
  /iphone|ipad|ipod/i.test(navigator.userAgent || "");

const isStandalone = () => {
  if (typeof window === "undefined") return false;
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) {
    return true;
  }
  if (window.navigator && window.navigator.standalone === true) return true;
  return false;
};

export const InstallButton = () => {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    if (isStandalone()) {
      setInstalled(true);
      return undefined;
    }
    const onBIP = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onBIP);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;

  const handleInstall = async () => {
    if (!deferred) return;
    try {
      deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice && choice.outcome === "accepted") {
        setInstalled(true);
      }
    } catch (_) {
      // Browser may throw if prompt was already used or dismissed too fast.
    }
    setDeferred(null);
  };

  if (deferred) {
    return (
      <div className="mt-8" data-testid="install-zone" data-state="prompt-ready">
        <button
          data-testid="install-pwa-btn"
          onClick={handleInstall}
          type="button"
          className="ss-cta-mode ss-cta-install"
        >
          <Smartphone className="w-5 h-5" strokeWidth={2.25} />
          Add DateWise to My Phone
        </button>
        <p
          className="mt-2 text-sm"
          style={{ color: "var(--ss-muted)" }}
          data-testid="install-pwa-hint"
        >
          Adds a home-screen icon. Opens like a regular app.
        </p>
      </div>
    );
  }

  const helpCopy = isIOS()
    ? "To add DateWise to your phone, open this site in Safari, tap the Share button (square with an arrow), then choose \u201CAdd to Home Screen.\u201D"
    : "To add DateWise to your phone, open this site in Chrome, tap the three-dot menu, then choose \u201CAdd to Home screen\u201D or \u201CInstall app.\u201D";

  return (
    <div className="mt-8" data-testid="install-zone" data-state="fallback">
      <button
        data-testid="install-help-toggle"
        onClick={() => setShowHelp((s) => !s)}
        type="button"
        className="ss-ghost-sm"
        aria-expanded={showHelp}
      >
        <HelpCircle className="w-3.5 h-3.5" strokeWidth={2.25} />
        How to install DateWise on my phone
      </button>
      {showHelp && (
        <p
          data-testid="install-helper-copy"
          className="mt-3 text-sm leading-relaxed pl-3 border-l-2"
          style={{ color: "var(--ss-ink-2)", borderColor: "var(--ss-gold)" }}
        >
          {helpCopy}
        </p>
      )}
    </div>
  );
};
